import * as React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  Paper,
  TextField,
  InputAdornment,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { useNavigate } from "react-router-dom";

import {
  listDeviceAssignments,
  listAdmissions,
} from "../api/deviceAssignmentPagessApi";
import { ROUTES } from "../../shared/constants/routes";

// adjust these imports if your project paths differ
import { prettyError } from "../utils/prettyError";
import { Colors } from "../../utils/Colors";

function pickFirst(...values) {
  return values.find((v) => v !== undefined && v !== null && v !== "");
}

function fmt(v) {
  if (v === undefined || v === null || v === "") return "—";
  return String(v);
}

function asString(v) {
  if (v === undefined || v === null) return "";
  return String(v);
}

// ---------------- admission helpers ----------------
function getAdmissionId(item) {
  return item?.admission_id ?? item?.id ?? item?.uuid ?? "";
}

function getAdmissionNo(item) {
  return item?.admission_no ?? item?.admission_number ?? item?.admission_code ?? "";
}

function getAdmissionPatientName(item) {
  return (
    item?.patient_name ??
    item?.patient?.full_name ??
    item?.patient?.name ??
    item?.full_name ??
    ""
  );
}

function getAdmissionPatientUhid(item) {
  return (
    item?.patient_uhid_out ??
    item?.patient_uhid ??
    item?.uhid ??
    item?.uhid_no ??
    item?.uhid_number ??
    item?.patient?.uhid ??
    ""
  );
}

function getAdmissionHospitalName(item) {
  return item?.organization_name ?? item?.hospital_name ?? item?.hospital ?? "";
}

function getAdmissionWardName(item) {
  return item?.ward_unit ?? item?.ward_name ?? item?.ward ?? "";
}

function getAdmissionBedName(item) {
  return item?.bed_number ?? item?.bed_name ?? item?.bed ?? "";
}

// ---------------- assignment helpers ----------------
function getAssignmentId(item) {
  return item?.device_assignment_id ?? item?.assignment_id ?? item?.id ?? item?.uuid ?? "";
}

function getAssignmentAdmissionId(item) {
  return (
    item?.admission_id ??
    item?.admission?.admission_id ??
    item?.admission?.id ??
    item?.admission ??
    ""
  );
}

function getAssignmentDeviceSerial(item) {
  return (
    item?.device_serial ??
    item?.serial_number ??
    item?.serial ??
    item?.device?.device_serial ??
    item?.device?.serial_number ??
    item?.device?.serial ??
    item?.device_code ??
    item?.device_name ??
    ""
  );
}

function makeMap(rows, getIdFn) {
  const map = {};
  (rows || []).forEach((row) => {
    const id = getIdFn(row);
    if (id !== undefined && id !== null && id !== "") {
      map[asString(id)] = row;
    }
  });
  return map;
}

function normalizeAssignment(row, admissionsMap) {
  const admissionRef = getAssignmentAdmissionId(row);
  const admissionObj =
    typeof admissionRef === "object" && admissionRef !== null
      ? admissionRef
      : admissionsMap[asString(admissionRef)] || null;

  return {
    raw: row,
    assignmentId: getAssignmentId(row),
    admissionNo: admissionObj ? getAdmissionNo(admissionObj) : "",
    deviceSerial: getAssignmentDeviceSerial(row),
    patientName: admissionObj ? getAdmissionPatientName(admissionObj) : "",
    patientUhid: admissionObj ? getAdmissionPatientUhid(admissionObj) : "",
    hospitalName: admissionObj ? getAdmissionHospitalName(admissionObj) : "",
  };
}

export default function DeviceAssignmentListPage() {
  const navigate = useNavigate();

  const [rows, setRows] = React.useState([]);
  const [filteredRows, setFilteredRows] = React.useState([]);
  const [search, setSearch] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  async function loadData() {
    setLoading(true);
    setError("");

    try {
      const [assignments, admissions] = await Promise.all([
        listDeviceAssignments(),
        listAdmissions(),
      ]);

      const admissionsMap = makeMap(admissions, getAdmissionId);

      const normalized = (assignments || []).map((row) =>
        normalizeAssignment(row, admissionsMap)
      );

      setRows(normalized);
      setFilteredRows(normalized);
    } catch (err) {
      setError(prettyError(err));
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    loadData();
  }, []);

  React.useEffect(() => {
    const q = asString(search).trim().toLowerCase();

    if (!q) {
      setFilteredRows(rows);
      return;
    }

    setFilteredRows(
      rows.filter((r) =>
        [r.admissionNo, r.deviceSerial, r.patientName, r.patientUhid, r.hospitalName]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q)
      )
    );
  }, [search, rows]);

  function handleRowClick(row) {
    if (!row?.assignmentId) return;
    navigate(ROUTES.DEVICE_ASSIGNMENT_DETAILS(row.assignmentId));
  }

  return (
    <Box sx={{ p: 2, background: Colors?.bg?.app || "#f7f9fb", minHeight: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Card
          variant="outlined"
          sx={{
            width: "100%",
            maxWidth: 1000,
            borderRadius: 3,
            borderColor: Colors?.border?.light || "#d9e1e7",
            background: Colors?.bg?.paper || "#fff",
            boxShadow: Colors?.shadow?.brand || "0 8px 18px rgba(11,60,63,0.08)",
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 900, color: Colors?.text?.title || "#16343b" }}>
              Device Assignment List
            </Typography>

            <Typography sx={{ mt: 0.5, fontSize: 13, color: Colors?.text?.sub || "#5f6f78" }}>
              Showing Admission Number and Device Serial Number.
            </Typography>

            <Divider sx={{ my: 2, borderColor: Colors?.border?.topLine || "#e5edf2" }} />

            {error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : null}

            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                borderColor: Colors?.border?.light || "#d9e1e7",
              }}
            >
              <Box sx={{ p: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by admission number or serial..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchOutlinedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Divider />

              {loading ? (
                <Box
                  sx={{
                    p: 3,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CircularProgress size={22} />
                  <Typography>Loading device assignments...</Typography>
                </Box>
              ) : filteredRows.length === 0 ? (
                <Box sx={{ p: 3 }}>
                  <Alert severity="info">No device assignments found.</Alert>
                </Box>
              ) : (
                <TableContainer sx={{ maxHeight: 560 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 900 }}>Admission Number</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>Device Serial Number</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredRows.map((row, idx) => (
                        <TableRow
                          key={`${row?.assignmentId ?? idx}`}
                          hover
                          onClick={() => handleRowClick(row)}
                          sx={{
                            cursor: "pointer",
                            "&:hover": { backgroundColor: Colors?.bg?.hover || "#f5fbfc" },
                          }}
                        >
                          <TableCell>{fmt(row.admissionNo)}</TableCell>
                          <TableCell>{fmt(row.deviceSerial)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}